import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  completeGoogleWorkspaceAuth,
  getCompanyIntegrationsHealth,
} from '../api';
import {
  DRIVE_CHECKBOX_HINT,
  isDriveFileAccessDenied,
} from '../crm/utils/googleWorkspaceUtils';

const GW_CALLBACK_URL = `${window.location.origin}/integrations/google-workspace/callback`;

const STATUS = {
  VERIFYING: 'verifying',
  SUCCESS: 'success',
  ERROR: 'error',
};

export function GoogleWorkspaceCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState(STATUS.VERIFYING);
  const [errorMessage, setErrorMessage] = useState('');
  const hasRun = useRef(false);

  useEffect(() => {
    // Guard against React StrictMode double-mount
    if (hasRun.current) return;
    hasRun.current = true;

    const run = async () => {
      // Phase 1: Check for user-cancelled consent
      const oauthError = searchParams.get('error');
      if (oauthError) {
        if (window.opener && !window.opener.closed) {
          try {
            window.opener.postMessage(
              { type: 'gw-auth-done', status: 'cancelled' },
              '*',
            );
          } catch (e) {
            console.warn('postMessage to opener failed:', e);
          }
          setTimeout(() => {
            window.close();
          }, 300);
          return;
        }
        toast('Google sign-in was cancelled.', {
          icon: 'ℹ️',
          style: {
            borderRadius: '10px',
            background: '#fff',
            color: '#374151',
            border: '1px solid #f3f4f6',
            fontSize: '14px',
          },
        });
        navigate('/integrations/google-workspace', { replace: true });
        return;
      }

      // Phase 2: Extract code + state
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (!code || !state) {
        setStatus(STATUS.ERROR);
        setErrorMessage(
          'Missing authorization parameters. Please try connecting again.',
        );
        return;
      }

      // Phase 3: Retrieve companyId from sessionStorage
      const companyId =
        sessionStorage.getItem('gw_company_id') ||
        localStorage.getItem('gw_company_id');
      if (!companyId) {
        // Fallback to localStorage
        const fallback =
          (() => {
            try {
              const u = localStorage.getItem('user');
              return u ? JSON.parse(u)?.company_id : null;
            } catch {
              return null;
            }
          })() || localStorage.getItem('company_id');

        if (!fallback) {
          navigate('/integrations', { replace: true });
          return;
        }
      }

      const resolvedCompanyId =
        companyId ||
        (() => {
          try {
            const u = localStorage.getItem('user');
            return u ? JSON.parse(u)?.company_id : null;
          } catch {
            return null;
          }
        })() ||
        localStorage.getItem('company_id');

      // Phase 4: Exchange code for tokens via backend
      try {
        await completeGoogleWorkspaceAuth({
          companyId: resolvedCompanyId,
          payload: {
            code,
            state,
            redirect_uri: GW_CALLBACK_URL,
          },
        });

        // Clean up
        sessionStorage.removeItem('gw_company_id');
        localStorage.removeItem('gw_company_id');

        // Phase 5: Refetch integrations health snapshot so badge flips to healthy immediately
        try {
          await getCompanyIntegrationsHealth(resolvedCompanyId);
        } catch (healthErr) {
          console.warn(
            'Health check prefetch after OAuth complete failed:',
            healthErr,
          );
        }

        // Invalidate relevant caches
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ['company-integrations-health', resolvedCompanyId],
          }),
          queryClient.invalidateQueries({
            queryKey: ['company-integrations', resolvedCompanyId],
          }),
          queryClient.invalidateQueries({
            queryKey: ['google-workspace-status', resolvedCompanyId],
          }),
          queryClient.invalidateQueries({
            queryKey: ['integration-health'],
          }),
        ]);

        // Success — toast + redirect
        toast.success('Google Workspace connected successfully!', {
          duration: 4000,
        });
        setStatus(STATUS.SUCCESS);
        if (window.opener && !window.opener.closed) {
          try {
            window.opener.postMessage(
              { type: 'gw-auth-done', status: 'success' },
              '*',
            );
          } catch (e) {
            console.warn('postMessage to opener failed:', e);
          }
          try {
            localStorage.setItem('gw_auth_event', String(Date.now()));
          } catch {
            // ignore storage errors
          }
          setTimeout(() => {
            window.close();
          }, 400);
          return;
        }
        navigate('/integrations/google-workspace', { replace: true });
      } catch (err) {
        sessionStorage.removeItem('gw_company_id');
        localStorage.removeItem('gw_company_id');
        const rawDetail =
          err?.response?.data?.detail || err?.response?.data?.message;
        const detailText =
          typeof rawDetail === 'string'
            ? rawDetail
            : 'Could not connect Google Workspace. The authorization session may have expired.';
        const message = isDriveFileAccessDenied(err)
          ? DRIVE_CHECKBOX_HINT
          : detailText;
        setStatus(STATUS.ERROR);
        setErrorMessage(message);
        if (window.opener && !window.opener.closed) {
          try {
            window.opener.postMessage(
              {
                type: 'gw-auth-done',
                status: 'error',
                message,
              },
              '*',
            );
          } catch (e) {
            console.warn('postMessage to opener failed:', e);
          }
          setTimeout(() => {
            window.close();
          }, 500);
        }
      }
    };

    run();
  }, [navigate, searchParams, queryClient]);

  // ── Loading state ──────────────────────────────────────────────────────────
  if (status === STATUS.VERIFYING || status === STATUS.SUCCESS) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-10 flex flex-col items-center gap-6 max-w-sm w-full text-center">
          {/* Animated Google G */}
          <div className="w-16 h-16 rounded-2xl bg-white shadow-md border border-gray-100 flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-10 h-10">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
          </div>

          {/* Spinner */}
          <div className="relative w-10 h-10">
            <svg
              className="animate-spin w-10 h-10 text-[#4285F4]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <circle cx="12" cy="12" r="10" strokeOpacity="0.15" />
              <path strokeLinecap="round" d="M12 2a10 10 0 0 1 10 10" />
            </svg>
          </div>

          <div>
            <p className="text-[16px] font-semibold text-gray-900">
              Verifying permissions...
            </p>
            <p className="text-[13px] text-gray-500 mt-1">
              Connecting your Google Workspace account. Please wait.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-10 flex flex-col items-center gap-6 max-w-sm w-full text-center">
        {/* Error icon */}
        <div className="w-16 h-16 rounded-full bg-red-50 border border-red-100 flex items-center justify-center text-red-500">
          <svg
            className="w-8 h-8"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>

        <div>
          <p className="text-[17px] font-semibold text-gray-900">
            Connection Failed
          </p>
          <p className="text-[13px] text-gray-500 mt-2 leading-relaxed max-w-xs">
            {errorMessage}
          </p>
        </div>

        <button
          id="gw-callback-back-btn"
          onClick={() =>
            navigate('/integrations/google-workspace', { replace: true })
          }
          className="px-5 py-2.5 text-[13px] font-semibold text-white bg-gray-900 hover:bg-gray-700 rounded-lg transition-colors"
        >
          Back to Integrations
        </button>
      </div>
    </div>
  );
}
